import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import { LockKeyhole } from "lucide-react";
import * as ReactRouter from "react-router";
import { data, Link } from "react-router";
import { Button } from "~/app/components/ui/button";
import * as Paths from "~/app/paths";

const PAGE_SIZE = 24;
const LOCKED_PAGE_FROM = 11;

type IndexFilters = {
  category: string | null;
  sourceStatus: string | null;
};

function parsePage(rawValue: string | null): number {
  if (!rawValue) return 1;
  const parsed = Number.parseInt(rawValue, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return parsed;
}

function normalizeFilterValue(rawValue: string | null): string | null {
  if (!rawValue) return null;
  const normalized = rawValue.replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  return normalized.slice(0, 120);
}

function parseFilters(url: URL): IndexFilters {
  return {
    category: normalizeFilterValue(url.searchParams.get("category")),
    sourceStatus: normalizeFilterValue(url.searchParams.get("sourceStatus")),
  };
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  const asDate = new Date(value);
  if (Number.isNaN(asDate.getTime())) return "-";
  return asDate.toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatShare(value: number): string {
  return value.toLocaleString("pl-PL", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function buildIndexHref(params?: {
  page?: number;
  category?: string | null;
  sourceStatus?: string | null;
}): string {
  const page = Math.max(1, Math.trunc(params?.page ?? 1));
  const search = new URLSearchParams();
  const category = normalizeFilterValue(params?.category ?? null);
  const sourceStatus = normalizeFilterValue(params?.sourceStatus ?? null);

  if (category) search.set("category", category);
  if (sourceStatus) search.set("sourceStatus", sourceStatus);
  if (page > 1) search.set("page", String(page));

  const query = search.toString();
  return query
    ? `/interpretacje-podatkowe?${query}`
    : "/interpretacje-podatkowe";
}

function getFiltersLabel(filters: IndexFilters): string | null {
  const parts: string[] = [];
  if (filters.category) parts.push(`kategoria: ${filters.category}`);
  if (filters.sourceStatus) parts.push(`status: ${filters.sourceStatus}`);
  return parts.length > 0 ? parts.join(", ") : null;
}

export const meta: ReactRouter.MetaFunction<typeof loader> = ({ data }) => {
  const total = data?.total ?? 0;
  const filtersLabel = data ? getFiltersLabel(data.filters) : null;
  const pagePart = data && data.page > 1 ? ` - strona ${data.page}` : "";
  const filterPart = filtersLabel ? ` (${filtersLabel})` : "";
  const title = `Interpretacje podatkowe MF${pagePart}${filterPart} | AnyLawyer`;
  const descriptionBase =
    total > 0
      ? `Publiczny indeks interpretacji podatkowych MF. Dostepnych wpisow: ${total}.`
      : "Publiczny indeks interpretacji podatkowych MF.";
  const description = filtersLabel
    ? `Filtry: ${filtersLabel}. ${descriptionBase}`
    : descriptionBase;
  const canonicalUrl =
    data?.canonicalUrl ?? "https://anylawyer.com/interpretacje-podatkowe";
  const origin = new URL(canonicalUrl).origin;

  const itemList = (data?.items ?? []).map((item, index) => ({
    "@type": "ListItem",
    position:
      ((data?.page ?? 1) - 1) * (data?.pageSize ?? PAGE_SIZE) + index + 1,
    url: `${origin}${Paths.eurekaSeoDetail(item.slug)}`,
    name: item.signature ?? item.thesis ?? `Interpretacja ${item.eurekaId}`,
  }));

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: title,
        description,
        inLanguage: "pl-PL",
      },
      {
        "@type": "ItemList",
        "@id": `${canonicalUrl}#itemlist`,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: itemList.length,
        itemListElement: itemList,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Interpretacje podatkowe MF",
            item: `${origin}/interpretacje-podatkowe`,
          },
        ],
      },
    ],
  };

  return [
    { title },
    { name: "description", content: description },
    { tagName: "link", rel: "canonical", href: canonicalUrl },
    { property: "og:type", content: "website" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: canonicalUrl },
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { "script:ld+json": jsonLd },
  ];
};

export async function loader(args: ReactRouter.LoaderFunctionArgs) {
  const {
    createWeakEtag,
    EUREKA_SEO_HTML_CACHE_CONTROL,
    getEurekaSeoIndexPage,
    getEurekaSeoSnapshotVersion,
    getEurekaSeoTopCategories,
    getEurekaSeoTopSourceStatuses,
    requestMatchesEtag,
  } = await import("~/app/lib/eureka-seo.server");

  const url = new URL(args.request.url);
  const filters = parseFilters(url);
  const requestedPage = parsePage(url.searchParams.get("page"));
  const requiresLogin = requestedPage >= LOCKED_PAGE_FROM;

  const [pageData, topCategories, sourceStatuses, version] = await Promise.all([
    getEurekaSeoIndexPage({
      page: requestedPage,
      pageSize: PAGE_SIZE,
      filters,
    }),
    getEurekaSeoTopCategories({ limit: 5 }),
    getEurekaSeoTopSourceStatuses({ limit: 6 }),
    getEurekaSeoSnapshotVersion(),
  ]);

  const payload = {
    ...pageData,
    items: requiresLogin ? [] : pageData.items,
    requiresLogin,
    filters,
    topCategories,
    fastFilters: {
      categories: topCategories.map((row) => ({
        value: row.category,
        count: row.count,
      })),
      sourceStatuses,
    },
    canonicalUrl: `${url.origin}${buildIndexHref({
      page: pageData.page,
      category: filters.category,
      sourceStatus: filters.sourceStatus,
    })}`,
  };

  const etag = createWeakEtag(
    [
      "eureka-seo:index",
      version,
      `p${payload.page}`,
      `locked${payload.requiresLogin ? "1" : "0"}`,
      `cat:${payload.filters.category ?? ""}`,
      `status:${payload.filters.sourceStatus ?? ""}`,
    ].join(":")
  );
  const headers = new Headers({
    "Cache-Control": EUREKA_SEO_HTML_CACHE_CONTROL,
    ETag: etag,
  });

  if (requestMatchesEtag(args.request, etag)) {
    return new Response(null, {
      status: 304,
      headers,
    });
  }

  return data(payload, { headers });
}

export default function EurekaSeoIndexPage() {
  const loaderData = ReactRouter.useLoaderData<typeof loader>();
  const blurClass = loaderData.requiresLogin
    ? "blur-md pointer-events-none select-none"
    : "";
  const placeholderCards = Array.from({ length: 8 }, (_, index) => index);
  const filtersLabel = getFiltersLabel(loaderData.filters);

  const feedItems = loaderData.items.flatMap((item, index) => {
    const cards = [
      <article
        key={item.eurekaId}
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-slate-300"
      >
        <p className="text-xs uppercase tracking-wide text-slate-500">
          ID Eureka: {item.eurekaId}
        </p>

        <h2 className="mt-2 text-lg font-semibold leading-snug text-slate-900">
          <Link
            to={Paths.eurekaSeoDetail(item.slug)}
            className="hover:text-emerald-700"
          >
            {item.signature ?? item.thesis ?? `Interpretacja ${item.eurekaId}`}
          </Link>
        </h2>

        {item.bodySummary ? (
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            {item.bodySummary}
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500">
          <span>
            Data publikacji: {formatDate(item.publishedAt ?? item.issuedAt)}
          </span>
          <span>Kategoria: {item.category ?? "-"}</span>
        </div>
      </article>,
    ];

    if ((index + 1) % 10 === 0 && index + 1 < loaderData.items.length) {
      cards.push(
        <aside
          key={`cta-${index}`}
          className="rounded-xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-5 text-white shadow-sm"
        >
          <p className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-100">
            AnyLawyer Pro
          </p>
          <h3 className="mt-3 text-lg font-semibold leading-snug sm:text-xl">
            Analizuj kolejne interpretacje szybciej
          </h3>
          <p className="mt-2 max-w-2xl text-sm text-slate-200">
            Odblokuj AI research, cytowania i workflow dokumentowy w jednym
            miejscu.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <SignedOut>
              <SignInButton mode="modal">
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100"
                >
                  Zaloguj sie do AnyLawyer
                </button>
              </SignInButton>
              <Link
                to="/pricing"
                className="inline-flex items-center rounded-md border border-white/35 px-4 py-2 text-sm font-medium text-white hover:bg-white/10"
              >
                Zobacz plan
              </Link>
            </SignedOut>
            <SignedIn>
              <Link
                to={Paths.app()}
                className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-100"
              >
                Przejdz do aplikacji
              </Link>
            </SignedIn>
          </div>
        </aside>
      );
    }

    return cards;
  });

  return (
    <section className="bg-slate-50 min-h-screen">
      <div className="mx-auto relative max-w-6xl px-4 py-10 sm:px-6">
        {loaderData.requiresLogin ? (
          <div className="absolute inset-0 z-30 flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-700">
                <LockKeyhole className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-xl font-semibold text-slate-900">
                Dalsze strony wymagaja logowania
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Od strony {LOCKED_PAGE_FROM} prezentujemy tylko podglad. Zaloguj
                sie do AnyLawyer, aby odblokowac pelne przegladanie danych.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <SignedOut>
                  <SignInButton mode="modal">
                    <Button className="rounded-lg bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)]">
                      Zaloguj sie do AnyLawyer
                    </Button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <Button asChild className="rounded-lg">
                    <Link to={Paths.app()}>Przejdz do aplikacji</Link>
                  </Button>
                </SignedIn>
              </div>
            </div>
          </div>
        ) : null}

        <div className={blurClass}>
          <header className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              AnyLawyer Public Data
            </p>
            <h1 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
              Interpretacje podatkowe MF
            </h1>
            <p className="mt-3 max-w-3xl text-sm text-slate-600 sm:text-base">
              Indeks najwazniejszych fragmentow interpretacji z bazy Eureka:
              teza, streszczenie oraz tresc. Tresc jest pobierana bezposrednio z
              bazy i serwowana z agresywnym cache HTTP.
            </p>

            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg bg-slate-100 px-4 py-3">
                <div className="text-xs uppercase text-slate-500">Wpisy</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">
                  {loaderData.total.toLocaleString("pl-PL")}
                </div>
              </div>
              <div className="rounded-lg bg-slate-100 px-4 py-3">
                <div className="text-xs uppercase text-slate-500">
                  Ostatnia zmiana
                </div>
                <div className="mt-1 text-lg font-semibold text-slate-900">
                  {formatDate(loaderData.lastModified)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-100 px-4 py-3">
                <div className="text-xs uppercase text-slate-500">Strona</div>
                <div className="mt-1 text-lg font-semibold text-slate-900">
                  {loaderData.page}/{loaderData.totalPages}
                </div>
              </div>
            </div>

            {loaderData.topCategories.length > 0 ? (
              <div className="mt-6">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  Top 5 kategorii
                </h2>
                <div className="mt-3 grid gap-3 md:grid-cols-5">
                  {loaderData.topCategories.map((row) => {
                    const isActive =
                      loaderData.filters.category?.toLowerCase() ===
                      row.category.toLowerCase();

                    return (
                      <Link
                        key={row.category}
                        to={buildIndexHref({
                          category: isActive ? null : row.category,
                          sourceStatus: loaderData.filters.sourceStatus,
                        })}
                        className={`rounded-lg border px-3 py-3 text-sm transition-colors ${
                          isActive
                            ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                            : "border-slate-200 bg-slate-50 text-slate-800 hover:border-slate-300"
                        }`}
                      >
                        <div className="font-semibold">{row.category}</div>
                        <div className="mt-1 text-xs">
                          {row.count.toLocaleString("pl-PL")} (
                          {formatShare(row.share)}%)
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Fast filters
              </h2>

              <div className="mt-3 flex flex-wrap gap-2">
                {loaderData.fastFilters.categories.map((option) => {
                  const isActive =
                    loaderData.filters.category?.toLowerCase() ===
                    option.value.toLowerCase();
                  return (
                    <Link
                      key={`category:${option.value}`}
                      to={buildIndexHref({
                        category: isActive ? null : option.value,
                        sourceStatus: loaderData.filters.sourceStatus,
                      })}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? "border-emerald-300 bg-emerald-100 text-emerald-900"
                          : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                      }`}
                    >
                      Kategoria: {option.value} (
                      {option.count.toLocaleString("pl-PL")})
                    </Link>
                  );
                })}

                {loaderData.fastFilters.sourceStatuses.map((option) => {
                  const isActive =
                    loaderData.filters.sourceStatus?.toLowerCase() ===
                    option.value.toLowerCase();
                  return (
                    <Link
                      key={`status:${option.value}`}
                      to={buildIndexHref({
                        category: loaderData.filters.category,
                        sourceStatus: isActive ? null : option.value,
                      })}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        isActive
                          ? "border-cyan-300 bg-cyan-100 text-cyan-900"
                          : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
                      }`}
                    >
                      Status: {option.value} (
                      {option.count.toLocaleString("pl-PL")})
                    </Link>
                  );
                })}

                {filtersLabel ? (
                  <Link
                    to={buildIndexHref()}
                    className="rounded-full border border-slate-400 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
                  >
                    Wyczysc filtry
                  </Link>
                ) : null}
              </div>

              {filtersLabel ? (
                <p className="mt-3 text-xs text-slate-600">
                  Aktywne filtry: {filtersLabel}
                </p>
              ) : null}
            </div>
          </header>

          {loaderData.requiresLogin ? (
            <section className="mt-8 grid gap-4">
              {placeholderCards.map((cardId) => (
                <article
                  key={cardId}
                  className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="h-3 w-40 rounded bg-slate-200" />
                  <div className="mt-3 h-5 w-5/6 rounded bg-slate-200" />
                  <div className="mt-4 space-y-2">
                    <div className="h-3 w-full rounded bg-slate-200" />
                    <div className="h-3 w-11/12 rounded bg-slate-200" />
                    <div className="h-3 w-4/5 rounded bg-slate-200" />
                  </div>
                </article>
              ))}
            </section>
          ) : loaderData.items.length === 0 ? (
            <section className="mt-8 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-600">
              Brak publicznych wpisow do wyswietlenia.
            </section>
          ) : (
            <section className="mt-8 grid gap-4">{feedItems}</section>
          )}

          <nav className="mt-8 flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
            {loaderData.page > 1 ? (
              <Link
                to={buildIndexHref({
                  page: loaderData.page - 1,
                  category: loaderData.filters.category,
                  sourceStatus: loaderData.filters.sourceStatus,
                })}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Poprzednia strona
              </Link>
            ) : (
              <span className="text-sm text-slate-400">Poprzednia strona</span>
            )}

            <span className="text-sm text-slate-600">
              Strona {loaderData.page} z {loaderData.totalPages}
            </span>

            {loaderData.page < loaderData.totalPages ? (
              <Link
                to={buildIndexHref({
                  page: loaderData.page + 1,
                  category: loaderData.filters.category,
                  sourceStatus: loaderData.filters.sourceStatus,
                })}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Nastepna strona
              </Link>
            ) : (
              <span className="text-sm text-slate-400">Nastepna strona</span>
            )}
          </nav>
        </div>
      </div>
    </section>
  );
}
